// ═══════════════════════════════════════════════════════════
// Data Residency — Region Registry (single source of truth)
// ═══════════════════════════════════════════════════════════
//
// Every other surface in the app derives from this file: wrangler.jsonc
// bindings, the `Bindings` interface (src/worker/types.ts), preflight's
// REQUIRED_BINDINGS (scripts/verify-bindings.mjs), the migration fan-out
// script (scripts/db-migrate-all.mjs), scripts/provision-regions.mjs, the
// install-time storage picker, and the Settings processing-region picker.
// Do not hardcode a region list anywhere else — add it here and derive.
//
// Platform facts (verified against live Cloudflare docs, not memory —
// D1 jurisdictions shipped Nov 2025, DO `us` jurisdiction shipped Jun 2026):
//   - D1 location hint:   `wrangler d1 create <name> --location=<hint>`
//                         best-effort placement, NOT a compliance guarantee.
//                         Hints: wnam enam weur eeur apac oc. No sam/afr/me.
//   - D1 jurisdiction:    `wrangler d1 create <name> --jurisdiction=<j>`
//                         hard guarantee. Values: eu, fedramp. Immutable.
//   - DO location hint:   `NS.get(id, { locationHint })` — best-effort,
//                         honored ONLY on the first get() for a given ID.
//                         Hints: wnam enam sam weur eeur apac apac-ne
//                         apac-se oc afr me.
//   - DO jurisdiction:    `NS.jurisdiction("eu").idFromName(name)` — hard
//                         guarantee. Values: eu, us, fedramp. Immutable
//                         per ID.
//
// A DO/D1 cannot be relocated after creation. "Changing region" always
// means deriving a *different* name/ID and draining the old instance —
// see src/worker/do/addressing.ts for how processing-region changes are
// implemented as an identity change, not a move.

/**
 * Data STORAGE regions — one real D1 database per entry (except LEGACY,
 * which is the pre-existing shared `DB` binding). Chosen once at install
 * time and immutable afterward (no migration engine in this phase).
 */
export interface StorageRegionConfig {
  /** Wrangler binding name declared in wrangler.jsonc / src/worker/types.ts */
  binding: string;
  /** D1 --location hint (best-effort), or null if this region uses a jurisdiction instead */
  hint: string | null;
  /** D1 --jurisdiction (hard guarantee), or null if this region is hint-only */
  jurisdiction: "eu" | "fedramp" | null;
  /** True only for jurisdiction-backed regions — safe to market as a compliance guarantee */
  compliance: boolean;
  /** Not offered in the install picker — the pre-existing single-DB tenants live here */
  hidden?: boolean;
  /** Pre-selected option in the install picker */
  default?: boolean;
}

export const STORAGE_REGIONS = {
  LEGACY: {
    binding: "DB",
    hint: null,
    jurisdiction: null,
    compliance: false,
    hidden: true,
  },
  ENAM: {
    binding: "DB_ENAM",
    hint: "enam",
    jurisdiction: null,
    compliance: false,
    default: true,
  },
  WNAM: { binding: "DB_WNAM", hint: "wnam", jurisdiction: null, compliance: false },
  WEUR: { binding: "DB_WEUR", hint: "weur", jurisdiction: null, compliance: false },
  EEUR: { binding: "DB_EEUR", hint: "eeur", jurisdiction: null, compliance: false },
  APAC: { binding: "DB_APAC", hint: "apac", jurisdiction: null, compliance: false },
  OC: { binding: "DB_OC", hint: "oc", jurisdiction: null, compliance: false },
  EU: { binding: "DB_EU", hint: null, jurisdiction: "eu", compliance: true },
} as const satisfies Record<string, StorageRegionConfig>;

export type StorageRegion = keyof typeof STORAGE_REGIONS;

/**
 * Data PROCESSING regions — Durable Object jurisdiction/location-hint
 * combinations. Customer-changeable at any time from Settings (System tab).
 * Unlike storage, no real infrastructure is "created" per region — these
 * just parameterize how a CommissionProcessor DO instance is addressed.
 */
export interface ProcessingRegionConfig {
  /** DO location hint (best-effort), or null for AUTO / jurisdiction-only regions */
  hint: string | null;
  /** DO jurisdiction (hard guarantee), or null if this region is hint-only */
  jurisdiction: "eu" | "us" | "fedramp" | null;
  /** True only for jurisdiction-backed regions — safe to market as a compliance guarantee */
  compliance: boolean;
  /** Pre-selected option — spawn near the first request, no hint applied */
  default?: boolean;
}

export const PROCESSING_REGIONS = {
  AUTO: { hint: null, jurisdiction: null, compliance: false, default: true },
  WNAM: { hint: "wnam", jurisdiction: null, compliance: false },
  ENAM: { hint: "enam", jurisdiction: null, compliance: false },
  SAM: { hint: "sam", jurisdiction: null, compliance: false },
  WEUR: { hint: "weur", jurisdiction: null, compliance: false },
  EEUR: { hint: "eeur", jurisdiction: null, compliance: false },
  APAC: { hint: "apac", jurisdiction: null, compliance: false },
  APAC_NE: { hint: "apac-ne", jurisdiction: null, compliance: false },
  APAC_SE: { hint: "apac-se", jurisdiction: null, compliance: false },
  OC: { hint: "oc", jurisdiction: null, compliance: false },
  AFR: { hint: "afr", jurisdiction: null, compliance: false },
  ME: { hint: "me", jurisdiction: null, compliance: false },
  JUR_EU: { hint: null, jurisdiction: "eu", compliance: true },
  JUR_US: { hint: null, jurisdiction: "us", compliance: true },
} as const satisfies Record<string, ProcessingRegionConfig>;

export type ProcessingRegion = keyof typeof PROCESSING_REGIONS;

/** Type-safe accessor — `as const satisfies` narrows each entry to only its own
 * literal keys, so optional fields declared on other entries (`hidden`,
 * `default`) don't appear on the union without a cast back to the full shape. */
export function storageConfig(region: StorageRegion): StorageRegionConfig {
  return STORAGE_REGIONS[region];
}
export function processingConfig(region: ProcessingRegion): ProcessingRegionConfig {
  return PROCESSING_REGIONS[region];
}

/** Storage regions actually offered in the install-time picker (LEGACY excluded). */
export const SELECTABLE_STORAGE_REGIONS = (Object.keys(STORAGE_REGIONS) as StorageRegion[]).filter(
  (r) => !storageConfig(r).hidden,
);

export const DEFAULT_STORAGE_REGION = (Object.keys(STORAGE_REGIONS) as StorageRegion[]).find(
  (r) => storageConfig(r).default,
) as StorageRegion;

export const DEFAULT_PROCESSING_REGION = (
  Object.keys(PROCESSING_REGIONS) as ProcessingRegion[]
).find((r) => processingConfig(r).default) as ProcessingRegion;

export function isStorageRegion(value: string): value is StorageRegion {
  return Object.prototype.hasOwnProperty.call(STORAGE_REGIONS, value);
}

export function isProcessingRegion(value: string): value is ProcessingRegion {
  return Object.prototype.hasOwnProperty.call(PROCESSING_REGIONS, value);
}

/**
 * Coarse continent grouping derived from a region's hint/jurisdiction —
 * used only to warn the customer when storage and processing land on
 * different continents (a real latency cost), never for compliance
 * decisions. Returns null for regions with no geographic signal (LEGACY, AUTO).
 */
const CONTINENT_BY_HINT: Record<string, string> = {
  wnam: "North America",
  enam: "North America",
  sam: "South America",
  weur: "Europe",
  eeur: "Europe",
  apac: "Asia-Pacific",
  "apac-ne": "Asia-Pacific",
  "apac-se": "Asia-Pacific",
  oc: "Oceania",
  afr: "Africa",
  me: "Middle East",
};
const CONTINENT_BY_JURISDICTION: Record<string, string> = {
  eu: "Europe",
  us: "North America",
};

function continentOf(cfg: { hint: string | null; jurisdiction: string | null }): string | null {
  if (cfg.jurisdiction) return CONTINENT_BY_JURISDICTION[cfg.jurisdiction] ?? null;
  if (cfg.hint) return CONTINENT_BY_HINT[cfg.hint] ?? null;
  return null;
}

export function storageContinent(region: StorageRegion): string | null {
  return continentOf(storageConfig(region));
}
export function processingContinent(region: ProcessingRegion): string | null {
  return continentOf(processingConfig(region));
}
